import { useState, useEffect } from 'react'
import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'

const inputCls =
  'w-full bg-transparent border border-gray-200 dark:border-white/10 rounded-none px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-gray-400 dark:focus:border-cinema-gold'
const labelCls = 'block text-[10px] font-mono uppercase tracking-wider text-gray-500 dark:text-cinema-muted mb-1.5'

/**
 * MentorProfile — mentor edits their own professional identity.
 * Read: GET /project-lab/mentor/me · Save: PUT /project-lab/mentor/me
 */
export default function MentorProfile() {
  const [mentor, setMentor] = useState(null)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.get('/project-lab/mentor/me')
      .then((r) => {
        const m = r.data.mentor
        setMentor(m)
        setForm({
          professional_name: m.professional_name || '',
          professional_title: m.professional_title || '',
          biography: m.biography || '',
          expertise: (m.expertise || []).join(', '),
          portfolio_url: m.portfolio_url || '',
          linkedin_url: m.linkedin_url || '',
          years_experience: m.years_experience ?? '',
        })
      })
      .catch(() => setMentor({}))
  }, [])

  if (form === null) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  const save = async () => {
    if (!form.professional_name.trim()) {
      toast.error('Professional name is required')
      return
    }
    setSaving(true)
    try {
      const res = await api.put('/project-lab/mentor/me', {
        professional_name: form.professional_name.trim(),
        professional_title: form.professional_title.trim() || null,
        biography: form.biography.trim() || null,
        expertise: form.expertise.split(',').map((s) => s.trim()).filter(Boolean),
        portfolio_url: form.portfolio_url.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
        years_experience: form.years_experience === '' ? null : Number(form.years_experience),
      })
      setMentor(res.data.mentor)
      toast.success('Profile saved')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-3xl space-y-8">
      <header>
        <p className="text-[10px] uppercase tracking-[0.25em] text-cinema-gold font-medium">Project Lab Mentor</p>
        <h1 className="font-display text-2xl text-white mt-1">Professional Profile</h1>
        <p className="text-xs text-cinema-muted mt-1">
          How participants and SMA administration see your professional identity.
        </p>
      </header>

      <div className="space-y-6 border-t border-white/10 pt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Professional Name *</label>
            <input className={inputCls} value={form.professional_name}
              onChange={(e) => setForm({ ...form, professional_name: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Professional Title</label>
            <input className={inputCls} placeholder="e.g. Creative Director · Photography"
              value={form.professional_title}
              onChange={(e) => setForm({ ...form, professional_title: e.target.value })} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Biography</label>
          <textarea className={`${inputCls} min-h-[120px]`} value={form.biography}
            onChange={(e) => setForm({ ...form, biography: e.target.value })} />
        </div>

        <div>
          <label className={labelCls}>Expertise (comma-separated)</label>
          <input className={inputCls} placeholder="Commercial portraiture, Brand photography"
            value={form.expertise}
            onChange={(e) => setForm({ ...form, expertise: e.target.value })} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Years of Experience</label>
            <input className={inputCls} type="number" min="0" value={form.years_experience}
              onChange={(e) => setForm({ ...form, years_experience: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Portfolio URL</label>
            <input className={inputCls} value={form.portfolio_url}
              onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>LinkedIn URL</label>
            <input className={inputCls} value={form.linkedin_url}
              onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} />
          </div>
        </div>

        <div className="pt-4 border-t border-white/10 flex items-center gap-4">
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-cinema-gold text-black text-xs font-semibold uppercase tracking-wider hover:bg-cinema-glow transition-colors disabled:opacity-50 rounded-none"
          >
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
          <span className="text-xs text-cinema-muted">{mentor?.email}</span>
        </div>
      </div>
    </div>
  )
}
